import { REPORTS_V2 } from '../../../../shared/featureFlags/flagNames';
import { useAddJobToNotificationQueue } from '../../../components/JobNotificationHub';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { get, post } from '../../../redux/api';
import type { Report } from '../../../../shared/model-typings';

type TaskResponse = {
  id?: number;
  jobToken: string;
  task: string;
  status?: string;
};

type QueueResponse = {
  report: Report;
  task: TaskResponse;
};

/**
 * Hook that returns a mutation to queue a public attribution report.
 * On success, shows a toast and registers the returned task with the
 * JobNotificationHub so a second toast fires when the job completes.
 *
 * The caller builds the attribution URL (including all report options as
 * query params). When REPORTS_V2 is enabled, the mutation POSTs to that URL;
 * otherwise it GETs.
 */
export function useQueuePublicAttributionReport() {
  const isReportsV2Enabled = useIsFeatureEnabled(REPORTS_V2);
  const addJob = useAddJobToNotificationQueue();

  return useToastedMutation({
    mutationFn: (url: string) => (isReportsV2Enabled
      ? post<Record<string, never>, QueueResponse>(url, {})
      : get<QueueResponse>(url)),
    onSuccess: (response) => {
      const task = response?.data?.task;
      if (task?.jobToken) {
        addJob(task.jobToken, task.task);
      }
    },
    successToastMessage: 'Your public report is being generated. You\'ll receive an email shortly.',
    errorToastMessage: 'Failed to queue public report',
  });
}
